import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import {
  Button,
  Chip,
  Input,
  Pagination,
  Select,
  SelectItem,
  Spinner,
} from '@heroui/react';
import { useQuery } from '@tanstack/react-query';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import SEOHead from '../components/SEOHead';
import { useLocale } from '../contexts/LocaleContext';
import { productService } from '../services/productService';
import { Product, ProductSearchParams } from '../types/product';
import {
  generateCanonicalUrl,
  generateCategoryBreadcrumbs,
} from '../utils/seoUtils';

const Search: React.FC = () => {
  const [urlSearchParams, setUrlSearchParams] = useSearchParams();
  const { currentLocale } = useLocale();

  const getCurrencySymbol = () => {
    const currencySymbols: Record<string, string> = {
      GBP: '£',
      USD: '$',
      EUR: '€',
      CAD: 'C$',
      AUD: 'A$',
      SEK: 'kr',
      JPY: '¥',
      NZD: 'NZ$',
    };
    return currencySymbols[currentLocale.preferredCurrency] || '£';
  };

  const initializeSearchParams = useCallback((): ProductSearchParams => {
    return {
      page: parseInt(urlSearchParams.get('page') || '1', 10),
      pageSize: parseInt(urlSearchParams.get('pageSize') || '12', 10),
      sortBy: urlSearchParams.get('sortBy') || 'name',
      sortOrder: urlSearchParams.get('sortOrder') || 'asc',
      searchTerm: urlSearchParams.get('searchTerm') || undefined,
      category: urlSearchParams.get('category') || undefined,
      material: urlSearchParams.get('material') || undefined,
      creator: urlSearchParams.get('creator') || undefined,
      minPrice: urlSearchParams.get('minPrice')
        ? parseFloat(urlSearchParams.get('minPrice')!)
        : undefined,
      maxPrice: urlSearchParams.get('maxPrice')
        ? parseFloat(urlSearchParams.get('maxPrice')!)
        : undefined,
      isFeatured:
        urlSearchParams.get('isFeatured') === 'true' ? true : undefined,
    };
  }, [urlSearchParams]);

  const [searchParams, setSearchParams] = useState<ProductSearchParams>(
    initializeSearchParams()
  );

  const [searchInputValue, setSearchInputValue] = useState<string>(
    searchParams.searchTerm || ''
  );

  useEffect(() => {
    const newParams = initializeSearchParams();
    const hasChanged = Object.keys(newParams).some(key => {
      const currentValue = searchParams[key as keyof ProductSearchParams];
      const newValue = newParams[key as keyof ProductSearchParams];
      return currentValue !== newValue;
    });
    if (hasChanged) {
      setSearchParams(newParams);
      setSearchInputValue(newParams.searchTerm || '');
    }
  }, [
    urlSearchParams,
    initializeSearchParams,
    searchParams,
    setSearchParams,
    searchInputValue,
    setSearchInputValue,
  ]);

  const isFirstSyncRef = useRef(true);

  useEffect(() => {
    const newUrlParams = new URLSearchParams();
    Object.entries(searchParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        newUrlParams.set(key, value.toString());
      }
    });
    const currentUrlParams = new URLSearchParams(
      window.location.search
    ).toString();
    const newUrlString = newUrlParams.toString();
    if (currentUrlParams !== newUrlString) {
      setUrlSearchParams(newUrlParams, {
        replace: isFirstSyncRef.current || isDebounceUpdateRef.current,
      });
      if (isFirstSyncRef.current) {
        isFirstSyncRef.current = false;
      }
    }
  }, [searchParams, setUrlSearchParams]);

  const isDebounceUpdateRef = useRef(false);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const currentTerm = searchParams.searchTerm || '';
      if (searchInputValue === currentTerm) return;
      isDebounceUpdateRef.current = true;
      setSearchParams(prev => ({
        ...prev,
        searchTerm: searchInputValue || undefined,
        page:
          (prev.searchTerm || '') !== (searchInputValue || '') ? 1 : prev.page,
      }));
      setTimeout(() => {
        isDebounceUpdateRef.current = false;
      }, 0);
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [searchInputValue, searchParams.searchTerm]);

  useEffect(() => {
    if (
      !isDebounceUpdateRef.current &&
      searchParams.searchTerm !== searchInputValue
    ) {
      setSearchInputValue(searchParams.searchTerm || '');
    }
  }, [searchInputValue, searchParams.searchTerm, setSearchInputValue]);

  const {
    data: products,
    isLoading: productsLoading,
    error: productsError,
  } = useQuery({
    queryKey: ['products', searchParams, currentLocale.preferredCountryCode],
    queryFn: () =>
      productService.getProducts({
        ...searchParams,
        deliveryCountry: currentLocale.preferredCountryCode,
      }),
  });

  const { data: categories, isLoading: categoriesLoading } = useQuery({
    queryKey: ['categories'],
    queryFn: () => productService.getCategories(),
  });

  const { data: materials, isLoading: materialsLoading } = useQuery({
    queryKey: ['materials'],
    queryFn: () => productService.getMaterials(),
  });

  const { data: creators, isLoading: creatorsLoading } = useQuery({
    queryKey: ['creators'],
    queryFn: () => productService.getCreators(),
  });

  const handleSearchInputChange = useCallback((value: string) => {
    setSearchInputValue(value);
  }, []);

  const handleFilterChange = (key: string, value: string) => {
    setSearchParams(prev => ({
      ...prev,
      [key]: value || undefined,
      page: 1,
    }));
  };

  const handleSortChange = (sortBy: string, sortOrder: string) => {
    setSearchParams(prev => ({
      ...prev,
      sortBy,
      sortOrder,
    }));
  };

  const handlePageChange = (page: number) => {
    setSearchParams(prev => ({
      ...prev,
      page,
    }));
  };

  const clearFilters = () => {
    setSearchInputValue('');
    setSearchParams({
      page: 1,
      pageSize: 12,
      sortBy: 'name',
      sortOrder: 'asc',
    });
  };

  const hasActiveFilters =
    searchParams.searchTerm ||
    searchParams.category ||
    searchParams.material ||
    searchParams.creator ||
    searchParams.minPrice ||
    searchParams.maxPrice;

  const generateSEOData = () => {
    let title = 'Miniatures Catalog';
    let description =
      'Browse our complete collection of premium 3D printed miniatures for tabletop gaming, D&D campaigns, and miniature painting.';
    let keywords =
      '3D printed miniatures, tabletop gaming, D&D miniatures, miniature painting, fantasy miniatures, sci-fi miniatures';
    let breadcrumbs = undefined;

    if (searchParams.category) {
      title = `${searchParams.category} Miniatures`;
      description = `Discover our collection of premium 3D printed ${searchParams.category.toLowerCase()} miniatures. Perfect for tabletop gaming, D&D campaigns, and miniature painting.`;
      keywords = `${
        searchParams.category
      } miniatures, 3D printed ${searchParams.category.toLowerCase()}, tabletop gaming, D&D miniatures`;
      breadcrumbs = generateCategoryBreadcrumbs(searchParams.category);
    } else if (searchParams.searchTerm) {
      title = `Search Results for "${searchParams.searchTerm}"`;
      description = `Find premium 3D printed miniatures matching "${searchParams.searchTerm}". High-quality prints for tabletop gaming and miniature painting.`;
      keywords = `${searchParams.searchTerm}, 3D printed miniatures, tabletop gaming, miniature painting`;
    } else if (searchParams.material) {
      title = `${searchParams.material} Miniatures`;
      description = `Premium 3D printed miniatures made from ${searchParams.material}. Perfect for tabletop gaming, D&D campaigns, and miniature painting.`;
      keywords = `${searchParams.material} miniatures, 3D printed miniatures, tabletop gaming, D&D miniatures`;
    } else if (searchParams.creator) {
      title = `${searchParams.creator} Miniatures`;
      description = `Discover premium 3D printed miniatures by ${searchParams.creator}. High-quality designs for tabletop gaming and miniature painting.`;
      keywords = `${searchParams.creator} miniatures, 3D printed miniatures, tabletop gaming, miniature painting`;
    }

    return { title, description, keywords, breadcrumbs };
  };

  const seoData = generateSEOData();

  return (
    <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
      <SEOHead
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        url={generateCanonicalUrl('/search')}
        type='website'
        breadcrumbs={seoData.breadcrumbs}
      />

      <div className='mb-8'>
        <h1 className='text-3xl lg:text-4xl font-bold text-gray-900 mb-4'>
          {seoData.title}
        </h1>
        <p className='text-gray-600 text-lg'>{seoData.description}</p>
      </div>

      <div className='bg-white rounded-lg shadow-sm border p-6 mb-8'>
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-4'>
          <Input
            placeholder='Search miniatures...'
            startContent={
              <MagnifyingGlassIcon className='w-5 h-5 text-gray-400' />
            }
            value={searchInputValue}
            onValueChange={handleSearchInputChange}
            className='col-span-1 md:col-span-2'
          />

          <Select
            placeholder='Category'
            selectedKeys={searchParams.category ? [searchParams.category] : []}
            items={categories || []}
            onSelectionChange={keys => {
              const value = Array.from(keys)[0] as string;
              handleFilterChange('category', value);
            }}
            isLoading={categoriesLoading}
          >
            {category => (
              <SelectItem key={category.name}>{category.name}</SelectItem>
            )}
          </Select>

          <Select
            placeholder='Material'
            selectedKeys={searchParams.material ? [searchParams.material] : []}
            items={materials || []}
            onSelectionChange={keys => {
              const value = Array.from(keys)[0] as string;
              handleFilterChange('material', value);
            }}
            isLoading={materialsLoading}
          >
            {material => (
              <SelectItem key={material.name}>{material.name}</SelectItem>
            )}
          </Select>

          <Select
            placeholder='Creator'
            selectedKeys={searchParams.creator ? [searchParams.creator] : []}
            items={creators || []}
            onSelectionChange={keys => {
              const value = Array.from(keys)[0] as string;
              handleFilterChange('creator', value);
            }}
            isLoading={creatorsLoading}
          >
            {creator => (
              <SelectItem key={creator.creatorName}>
                {creator.creatorName}
              </SelectItem>
            )}
          </Select>
        </div>

        <div className='grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4'>
          <Input
            type='number'
            placeholder='Min Price'
            startContent={getCurrencySymbol()}
            value={searchParams.minPrice?.toString() || ''}
            onValueChange={value => handleFilterChange('minPrice', value)}
          />
          <Input
            type='number'
            placeholder='Max Price'
            startContent={getCurrencySymbol()}
            value={searchParams.maxPrice?.toString() || ''}
            onValueChange={value => handleFilterChange('maxPrice', value)}
          />

          <Select
            placeholder='Sort by'
            selectedKeys={[searchParams.sortBy || 'name']}
            onSelectionChange={keys => {
              const value = Array.from(keys)[0] as string;
              handleSortChange(value, searchParams.sortOrder || 'asc');
            }}
          >
            <SelectItem key='name'>Name</SelectItem>
            <SelectItem key='price'>Price</SelectItem>
          </Select>

          <Select
            placeholder='Order'
            selectedKeys={[searchParams.sortOrder || 'asc']}
            onSelectionChange={keys => {
              const value = Array.from(keys)[0] as string;
              handleSortChange(searchParams.sortBy || 'name', value);
            }}
          >
            <SelectItem key='asc'>Ascending</SelectItem>
            <SelectItem key='desc'>Descending</SelectItem>
          </Select>

          {hasActiveFilters && (
            <Button
              variant='bordered'
              onPress={clearFilters}
              className='md:col-span-1'
            >
              Clear Filters
            </Button>
          )}
        </div>
      </div>

      {hasActiveFilters && (
        <div className='mb-6'>
          <div className='flex flex-wrap gap-2'>
            {searchParams.searchTerm && (
              <Chip
                onClose={() => handleFilterChange('searchTerm', '')}
                variant='flat'
                color='primary'
              >
                Search: {searchParams.searchTerm}
              </Chip>
            )}
            {searchParams.category && (
              <Chip
                onClose={() => handleFilterChange('category', '')}
                variant='flat'
                color='secondary'
              >
                Category: {searchParams.category}
              </Chip>
            )}
            {searchParams.material && (
              <Chip
                onClose={() => handleFilterChange('material', '')}
                variant='flat'
                color='success'
              >
                Material: {searchParams.material}
              </Chip>
            )}
            {searchParams.creator && (
              <Chip
                onClose={() => handleFilterChange('creator', '')}
                variant='flat'
                color='warning'
              >
                Creator: {searchParams.creator}
              </Chip>
            )}
          </div>
        </div>
      )}

      {productsLoading && (
        <div className='flex justify-center items-center py-12'>
          <Spinner size='lg' color='primary' />
          <span className='ml-3 text-gray-600'>Loading miniatures...</span>
        </div>
      )}

      {productsError && (
        <div className='text-center py-12'>
          <p className='text-red-600 text-lg'>
            Failed to load miniatures. Please try again later.
          </p>
        </div>
      )}

      {products && products.products && (
        <>
          <div className='mb-6'>
            <p className='text-gray-600'>
              Showing {products.products.length} of {products.totalCount}{' '}
              miniatures
            </p>
          </div>

          <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-8'>
            {products.products.map((product: Product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>

          {products.totalPages > 1 && (
            <div className='flex justify-center'>
              <Pagination
                total={products.totalPages}
                page={products.page}
                onChange={handlePageChange}
                showControls
                classNames={{
                  wrapper:
                    'gap-0 overflow-visible h-8 rounded border border-divider',
                  item: 'w-8 h-8 text-small rounded-none bg-transparent',
                  cursor:
                    'bg-gradient-to-b from-default-500 to-default-800 dark:from-default-300 dark:to-default-100 text-white font-bold',
                }}
              />
            </div>
          )}
        </>
      )}

      {products && products.products && products.products.length === 0 && (
        <div className='text-center py-12'>
          <div className='text-6xl mb-4'>🎲</div>
          <h3 className='text-xl font-semibold text-gray-900 mb-2'>
            No miniatures found
          </h3>
          <p className='text-gray-600 mb-4'>
            Try adjusting your search criteria or browse all miniatures.
          </p>
          {hasActiveFilters && (
            <Button color='primary' onPress={clearFilters}>
              Clear All Filters
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default Search;
